import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Button,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";

function ImageUploadModule({ module, onChange, onDelete, isEditable }) {
  // module expects: label, imageLimit, and images (array of URLs)
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [conditionalOpen, setConditionalOpen] = useState(false);

  const handleLabelChange = (e) => {
    onChange({ ...module, label: e.target.value });
  };

  const handleImageLimitChange = (e) => {
    const value = parseInt(e.target.value, 10);
    onChange({ ...module, imageLimit: isNaN(value) ? 0 : value });
  };

  const addImage = () => {
    const newImages = module.images ? [...module.images] : [];
    if (module.imageLimit && newImages.length >= module.imageLimit) return;
    newImages.push("");
    onChange({ ...module, images: newImages });
  };

  const updateImage = (index, value) => {
    const newImages = module.images ? [...module.images] : [];
    newImages[index] = value;
    onChange({ ...module, images: newImages });
  };

  const removeImage = (index) => {
    const newImages = module.images ? [...module.images] : [];
    newImages.splice(index, 1);
    onChange({ ...module, images: newImages });
  };

  const handleRequiredToggle = (e) => {
    onChange({ ...module, required: e.target.checked });
  };

  const handleAdminWorkflowToggle = (e) => {
    onChange({ ...module, approvalRequired: e.target.checked });
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      {/* Module Header */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          IMAGE UPLOAD MODULE
        </Typography>
        {isEditable && (
          <Box>
            <Tooltip title="Settings">
              <IconButton
                onClick={() => setSettingsOpen(!settingsOpen)}
                size="small"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Conditional Visibility">
              <IconButton
                onClick={() => setConditionalOpen(!conditionalOpen)}
                size="small"
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Module">
              <IconButton onClick={onDelete} size="small" color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Module Preview */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          padding: 2,
          backgroundColor: "#fafafa",
          marginBottom: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          {module.label || "Image Upload / Photo Gallery"}
        </Typography>
        <Grid container spacing={2}>
          {(module.images || Array(module.imageLimit || 3).fill("")).map(
            (img, index) => (
              <Grid item key={index}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    border: "1px dashed #ccc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fafafa",
                  }}
                >
                  {img ? (
                    <img
                      src={img}
                      alt={`Uploaded ${index + 1}`}
                      style={{ maxWidth: "100%", maxHeight: "100%" }}
                    />
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      Image {index + 1}
                    </Typography>
                  )}
                </Box>
                {isEditable && settingsOpen && (
                  <>
                    <TextField
                      label={`Image ${index + 1} URL`}
                      value={img}
                      onChange={(e) => updateImage(index, e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                    <IconButton
                      onClick={() => removeImage(index)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Grid>
            ),
          )}
        </Grid>
      </Box>

      {/* Settings Panel */}
      {isEditable && settingsOpen && (
        <Box sx={{ marginTop: 2 }}>
          <TextField
            label="Module Label"
            value={module.label || ""}
            onChange={handleLabelChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Image Limit"
            type="number"
            value={module.imageLimit || ""}
            onChange={handleImageLimitChange}
            fullWidth
            margin="dense"
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addImage}
            sx={{ marginTop: 1 }}
          >
            Add Image
          </Button>

          {/* New Status Dropdowns */}
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="editable-status-label">
                  Editable when in Status:
                </InputLabel>
                <Select
                  labelId="editable-status-label"
                  value={module.editableWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, editableWhenStatus: e.target.value })
                  }
                  label="Editable when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="visible-status-label">
                  Visible when in Status:
                </InputLabel>
                <Select
                  labelId="visible-status-label"
                  value={module.visibleWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, visibleWhenStatus: e.target.value })
                  }
                  label="Visible when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ marginTop: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={module.required || false}
                  onChange={handleRequiredToggle}
                  color="primary"
                />
              }
              label="Required"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={module.approvalRequired || false}
                  onChange={handleAdminWorkflowToggle}
                  color="primary"
                />
              }
              label="Admin Workflow"
            />
          </Box>
        </Box>
      )}
      {isEditable && conditionalOpen && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body2">
            Conditional Visibility settings...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default ImageUploadModule;
